import SettingsEmptyState from '../../components/settings/SettingsEmptyState';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCurrent, usePut } from '../../hooks/';
import { MwButton, MwSpinnerButton } from '../../components/ui';
import SettingListItem from '../../components/settings/SettingListItem';
import { setLang } from '../../store/langSlice';
import { updateSettings } from '../../store/settingSlice';

const GeneralSettings = ({ refreshTenant, filterDefineSetting }) => {
    const dispatch = useDispatch();
    const { data, loading, putData } = usePut();
    const { currentCompanyId, currentTenantId, currentSettings, currentLangId } = useCurrent();
    const [t, i18n] = useTranslation('global');
    const settingsFromStore = useMemo(
        () => currentSettings?.generalSettings || {},
        [currentSettings?.generalSettings]
    );
    const [generalSetting, setGeneralSetting] = useState(settingsFromStore);

    useEffect(() => {
        setGeneralSetting(settingsFromStore || {});
    }, [settingsFromStore]);

    useEffect(() => {
        if (data) {
            refreshTenant();
        }
    }, [data, refreshTenant]);

    const generalSettingEntries = useMemo(
        () => Object.entries(generalSetting || {}),
        [generalSetting]
    );

    const saveHandler = async () => {
        try {
            const generalSettingArray = Object.entries(generalSetting || {}).map(([key, value]) => ({
                name: key,
                value,
            }));

            const payload = {
                companyId: currentCompanyId,
                settingsGeneral: generalSettingArray,
            };

            await putData(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/general-setting/${currentTenantId}`,
                payload
            );

            dispatch(
                updateSettings({
                    typeSetting: 'general',
                    value: generalSetting,
                })
            );

            if (generalSetting.DEFAULT_LANG) {
                const langValue = generalSetting.DEFAULT_LANG;
                i18n.changeLanguage(langValue);
                dispatch(
                    setLang({
                        label: langValue === 'ar' ? 'Arabic' : 'English',
                        value: langValue,
                    })
                );
            }

            await refreshTenant();
        } catch (error) {
            console.error('Error saving settings:', error);
        }
    };

    if (generalSettingEntries.length === 0) {
        return <SettingsEmptyState title={t('setting.generalSettings')} />;
    }

    return (
        <div>
            <div className='flex flex-wrap items-center justify-between gap-3 px-1 text-[#251658]'>
                <div className='text-sm font-bold'>{t('setting.generalSettings')}</div>
                {loading ? (
                    <MwSpinnerButton />
                ) : (
                    <MwButton inGroup={true} size='sm' onClick={saveHandler} type='saveBtn'>
                        {t('setting.Save_changes')}
                    </MwButton>
                )}
            </div>

            <div className='my-3 rounded-lg border border-slate-200 bg-white p-3'>
                {generalSettingEntries.length > 0 ? (
                    <div className='flex flex-col gap-3'>
                        {generalSettingEntries.map(([settingName, settingValue]) => (
                            <SettingListItem
                                key={settingName}
                                settingName={settingName}
                                settingValue={settingValue}
                                filterDefineSetting={filterDefineSetting}
                                setGeneralSetting={setGeneralSetting}
                                generalSetting={generalSetting}
                                currentLangId={currentLangId}
                            />
                        ))}
                    </div>
                ) : (
                    <div className='rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-400'>
                        {currentLangId === 'ar' ? 'لا توجد بيانات' : 'No data'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GeneralSettings;
